import React, { useState } from 'react';
import { BookOpen, Clock, Check } from 'lucide-react';
import ScrollReveal from './ScrollReveal';

export default function CourseModules() {
    const [activeModule, setActiveModule] = useState(0);

    const modules = [
        {
            title: "Módulo I",
            hours: "432h",
            desc: "Fundamentos e Ciclo Básico",
            subjects: [
                "Anatomia e Fisiologia Humana (80h)",
                "Fundamentos de Enfermagem I (32h)",
                "Biossegurança e Ambiente (40h)",
                "Farmacologia Aplicada (40h)",
                "Psicologia, Ética e Nutrição (120h)",
                "Português e Matemática Aplicada (80h)",
                "Práticas Inovadoras (40h)"
            ]
        },
        {
            title: "Módulo II",
            hours: "388h",
            desc: "Atenção Clínica e Saúde Coletiva",
            subjects: [
                "Fundamentos de Enfermagem II (108h)",
                "Assistência a Pacientes (Tratamento Clínico I) (60h)",
                "Saúde Coletiva (80h)",
                "Emergências (40h)",
                "Microbiologia e Parasitologia (40h)",
                "Estágio Supervisionado (60h)"
            ]
        },
        {
            title: "Módulo III",
            hours: "340h",
            desc: "Saúde Materno-Infantil e Estágios",
            subjects: [
                "Obstetrícia e Puerpério (60h)",
                "Pediatria e Neonatologia (40h)",
                "Assistência a Pacientes (Tratamento Clínico II) (60h)",
                "Saúde do Idoso (40h)",
                "Estágio Saúde Coletiva (60h)",
                "Estágio Supervisionado (140h)"
            ]
        },
        {
            title: "Módulo IV",
            hours: "440h",
            desc: "Alta Complexidade e Conclusão",
            subjects: [
                "Enfermagem a Pacientes em Estado Crítico (60h)",
                "Enfermagem Cirúrgica (80h)",
                "Saúde Mental (40h)",
                "Cuidados Paliativos e Tanatologia (20h)",
                "Metodologia, Dicção e Oratória (40h)",
                "Estágio Supervisionado (SPA, SAMU, UTI, EMERGÊNCIA) (200h)"
            ]
        }
    ];

    return (
        <section id="grade-curricular" className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <ScrollReveal>
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-conectiva-navy mb-4">
                            Grade Curricular Completa
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto mb-6">
                            O curso é estruturado em 4 módulos sequenciais, somando 1.600 horas (1.200h teóricas e 400h de prática e estágio).
                        </p>
                        <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
                            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full font-semibold border border-green-200">
                                <Clock size={20} />
                                <span>Duração Total: 1.600 Horas</span>
                            </div>
                            <a
                                href="/pdf/GRADE_CURRICULAR _CONNECTIVA.pdf"
                                download="Grade_Curricular_Connectiva_Enfermagem.pdf"
                                className="inline-flex items-center gap-2 bg-transparent text-conectiva-navy px-4 py-2 rounded-full font-bold border border-conectiva-navy hover:bg-conectiva-navy hover:text-white transition duration-300"
                            >
                                Baixar Grade em PDF
                            </a>
                        </div>
                    </div>
                </ScrollReveal>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Menu Lateral dos Módulos */}
                    <div className="space-y-4">
                        {modules.map((mod, index) => (
                            <ScrollReveal key={index} delay={index * 0.1}>
                                <button
                                    onClick={() => setActiveModule(index)}
                                    className={`w-full text-left p-6 rounded-xl transition duration-300 border-2 ${activeModule === index
                                        ? 'border-conectiva-lime bg-conectiva-navy text-white shadow-lg transform scale-105'
                                        : 'border-gray-100 bg-gray-50 text-slate-800 hover:border-conectiva-lime/50 hover:bg-white'
                                        }`}
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="text-xl font-bold">{mod.title}</h3>
                                        <span className={`text-sm font-bold px-2 py-1 rounded ${activeModule === index ? 'bg-conectiva-lime text-conectiva-navy' : 'bg-gray-200 text-gray-700'}`}>
                                            {mod.hours}
                                        </span>
                                    </div>
                                    <p className={`text-sm ${activeModule === index ? 'text-gray-300' : 'text-gray-500'}`}>
                                        {mod.desc}
                                    </p>
                                </button>
                            </ScrollReveal>
                        ))}
                    </div>

                    {/* Conteúdo do Módulo Ativo */}
                    <div className="lg:col-span-2">
                        <ScrollReveal delay={0.3}>
                            <div className="bg-gray-50 p-8 rounded-2xl border border-gray-100 shadow-inner h-full transition-all">
                                <div className="flex items-center gap-3 mb-8 pb-4 border-b border-gray-200">
                                    <div className="p-3 bg-white rounded-lg shadow-sm text-conectiva-navy">
                                        <BookOpen size={28} />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900">Disciplinas - {modules[activeModule].title}</h3>
                                        <p className="text-gray-600">{modules[activeModule].desc}</p>
                                    </div>
                                </div>

                                <ul className="space-y-4">
                                    {modules[activeModule].subjects.map((subject, idx) => (
                                        <li key={idx} className="flex items-start gap-3 bg-white p-4 rounded-xl shadow-sm hover:shadow-md transition">
                                            <div className="mt-1 flex-shrink-0 text-conectiva-lime">
                                                <Check size={20} strokeWidth={3} />
                                            </div>
                                            <span className="text-gray-800 font-medium">{subject}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </ScrollReveal>
                    </div>
                </div>
            </div>
        </section>
    );
}
